/**
 * Dumb-компонент полей формы «Передача в производство».
 * Ничего не знает об API и глобальном состоянии — только props + колбэки.
 */
import { useMemo, useRef } from 'react';
import { PROJECT_STAGES, STAGE_DEADLINE, isDeadlineField, type DeadlineField } from '@stages/stages';
import { UserRecipientCombo, type RecipientUserItem } from '@/components/UserRecipientCombo/UserRecipientCombo';
import { DateAutofillInput } from '@/components/ui/DateAutofillInput/DateAutofillInput';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { SelectDropdown } from '@/components/ui/SelectDropdown/SelectDropdown';
import {
  PRODUCTION_HANDOFF_INTRO_PHRASES,
  type ProductionHandoffIntroPhrase,
} from '@/constants/productionHandoffIntroPhrases';

export interface ProductionHandoffEmailFieldsProps {
  recipients: RecipientUserItem[];
  onRecipientsChange: (items: RecipientUserItem[]) => void;
  ccUsers: RecipientUserItem[];
  onCcUsersChange: (items: RecipientUserItem[]) => void;
  /** Какой срок этапа уходит в письме */
  deadlineField: DeadlineField;
  onDeadlineFieldChange: (field: DeadlineField) => void;
  /** Дата выбранного срока, дд.мм.гггг */
  deadlineDate: string;
  onDeadlineDateChange: (value: string) => void;
  introPhrase: ProductionHandoffIntroPhrase;
  onIntroPhraseChange: (value: ProductionHandoffIntroPhrase) => void;
  commentNote: string;
  onCommentNoteChange: (value: string) => void;
  disabled?: boolean;
}

export function ProductionHandoffEmailFields({
  recipients,
  onRecipientsChange,
  ccUsers,
  onCcUsersChange,
  deadlineField,
  onDeadlineFieldChange,
  deadlineDate,
  onDeadlineDateChange,
  introPhrase,
  onIntroPhraseChange,
  commentNote,
  onCommentNoteChange,
  disabled,
}: ProductionHandoffEmailFieldsProps) {
  const recipientRef = useRef<HTMLDivElement>(null);
  const ccComboRef = useRef<HTMLDivElement>(null);

  const deadlineOptions = useMemo(
    () => PROJECT_STAGES.map((stage) => ({ value: STAGE_DEADLINE[stage].field, label: STAGE_DEADLINE[stage].label })),
    []
  );

  const introOptions = useMemo(
    () =>
      PRODUCTION_HANDOFF_INTRO_PHRASES.map((text) => ({
        value: text,
        label: text,
      })),
    []
  );

  return (
    <div className="review-check-email-modal__fields">
      <div className="review-check-email-modal__field">
        <UserRecipientCombo
          value={recipients}
          onChange={onRecipientsChange}
          placeholder="Кому"
          containerRef={recipientRef}
          disabled={disabled}
        />
      </div>
      <div className="review-check-email-modal__field">
        <UserRecipientCombo
          value={ccUsers}
          onChange={onCcUsersChange}
          placeholder="Копия"
          containerRef={ccComboRef}
          disabled={disabled}
        />
      </div>
      <div className="review-check-email-modal__field">
        <SelectDropdown
          fullWidth
          options={introOptions}
          value={introPhrase}
          onChange={(v) => onIntroPhraseChange(v as ProductionHandoffIntroPhrase)}
          disabled={disabled}
        />
      </div>
      <div className="review-check-email-modal__row review-check-email-modal__row_cols-2">
        <div className="review-check-email-modal__field">
          <SelectDropdown
            fullWidth
            options={deadlineOptions}
            value={deadlineField}
            onChange={(v) => {
              if (isDeadlineField(v)) onDeadlineFieldChange(v);
            }}
            disabled={disabled}
          />
        </div>
        <div className="review-check-email-modal__field">
          <DateAutofillInput
            value={deadlineDate}
            onChange={onDeadlineDateChange}
            placeholder="дд.мм.гггг"
            fullWidth
            disabled={disabled}
          />
        </div>
      </div>
      <div className="review-check-email-modal__field">
        <Textarea
          value={commentNote}
          onChange={(e) => onCommentNoteChange(e.target.value)}
          placeholder="Комментарий к письму"
          fullWidth
          minRows={1}
          maxRows={40}
          inputClassName="review-check-email-modal__comment-input"
          disabled={disabled}
        />
      </div>
    </div>
  );
}
